import ApplicationSession from './application/ApplicationSession.js';
import ApplicationToken from './application/ApplicationToken.js';
import ApplicationUser from './application/ApplicationUser.js';
import FeatureFlagManager from './FeatureFlagManager.js';
import PasswordResetExchange from './provider/PasswordResetExchange.js';
import PasswordResetToken from './provider/PasswordResetToken.js';
import PermissionManager from './PermissionManager.js';
import PreferencesManager from './PreferencesManager.js';
import ProviderSession from './provider/ProviderSession.js';
import ProviderUser from './provider/ProviderUser.js';
import SignUpTokenExchange from './provider/SignUpTokenExchange.js';
import LocalStorageCache from './lib/LocalStorageCache.js';
import { UNVERIFIED_CREDENTIALS, INVALID_CREDENTIALS, UNKNOWN_CREDENTIALS, EXISTING_CREDENTIALS, } from './lib/errors.js';
/**
 * @property {object} status
 * @property {boolean} status.isAuthenticating
 * @property {boolean} status.isCheckingForExistingSession
 * @property {boolean} status.isWorking
 * @property {PermissionManager} permissionManager
 * @property {PreferencesManager} preferencesManager
 */
class SessionManager {
    constructor() {
        this.status = {
            isAuthenticating: false,
            isCheckingForExistingSession: false,
            isWorking: false,
            isAuthenticated: false,
        };
        Object.defineProperties(this.status, {
            isWorking: {
                get() {
                    return this.isAuthenticating || this.isCheckingForExistingSession;
                },
            },
            isAuthenticated: {
                get: () => {
                    return !!this.session;
                },
            },
        });
        this.permissionManager = new PermissionManager();
        this.preferencesManager = new PreferencesManager();
        this.featureFlagManager = new FeatureFlagManager(this.preferencesManager, new LocalStorageCache());
    }
    get user() {
        return !!this.session ? this.session.user : null;
    }
    /**
     * @return {Promise<SessionManager>}
     */
    async checkForExistingSession() {
        this.status.isCheckingForExistingSession = true;
        return (ApplicationSession.check()
            .then((session) => {
            if (session)
                return this.setSession(session);
            return this;
        })
            .catch((error) => {
            var _a;
            // A 404 indicates no existing session
            if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 404)
                return this;
            return Promise.reject(error);
        })
            .finally(() => {
            this.status.isCheckingForExistingSession = false;
        }));
    }
    /**
     * @param {string} email
     * @param {string} password
     * @return {Promise<SessionManager>}
     * @throws {Error} [SessionManager.INVALID_CREDENTIALS]
     * @throws {Error} [SessionManager.UNKNOWN_CREDENTIALS]
     * @throws {Error} [SessionManager.UNVERIFIED_CREDENTIALS]
     */
    async signInWithEmail(email, password) {
        this.status.isAuthenticating = true;
        return (ProviderSession.create({ email, password })
            .catch((error) => {
            var _a;
            let err = error;
            const message = (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) > 399
                ? error.body.results.errorDescription
                : null);
            if (message.includes('ACCOUNT_NOT_VERIFIED')) {
                err = new Error(UNVERIFIED_CREDENTIALS, { cause: error });
            }
            else if (message.includes('Invalid credentials given.')) {
                err = new Error(INVALID_CREDENTIALS, { cause: error });
            }
            else if (message.includes('ACCOUNT_DOES_NOT_EXIST')) {
                err = new Error(UNKNOWN_CREDENTIALS, { cause: error });
            }
            throw err;
        })
            .then((providerSession) => {
            const headers = { Authorization: `Bearer ${providerSession.accessToken}` };
            return (ProviderUser.retrieve(providerSession.user, null, { headers })
                .then((providerUser) => ({
                providerSession,
                providerUser,
            })));
        })
            .then(({ providerSession, providerUser }) => {
            const props = {
                provider: 'sidewalk',
                token: providerSession.accessToken,
                response: {
                    id: providerUser.id,
                    first_name: providerUser.first_name,
                    last_name: providerUser.last_name,
                    email: providerUser.email,
                },
            };
            return ApplicationSession.signIn(props);
        })
            .then((applicationSession) => {
            return this.setSession(applicationSession);
        })
            .finally(() => {
            this.status.isAuthenticating = false;
        }));
    }
    /**
     * @param {string} email
     * @param {string} password
     * @param {object} fields
     * @param {string} fields.firstName
     * @param {string} fields.lastName
     * @param {string} [fields.invitation]
     * @return {Promise<SessionManager>}
     * @throws {Error} [SessionManager.UNVERIFIED_CREDENTIALS]
     * @throws {Error} [SessionManager.EXISTING_CREDENTIALS]
     */
    async signUpWithEmail(email, password, fields = { firstName: '', lastName: '' }) {
        this.status.isAuthenticating = true;
        const props = { email, password, ...fields };
        return (ProviderUser.create(props)
            .then((user) => {
            if (user.is_active) {
                return this.signInWithEmail(email, password);
            }
            throw new Error(UNVERIFIED_CREDENTIALS);
        })
            .catch((error) => {
            var _a, _b;
            const signUpMessage = ((_b = (_a = error.body) === null || _a === void 0 ? void 0 : _a.errors) !== null && _b !== void 0 ? _b : [])[0];
            if (signUpMessage === null || signUpMessage === void 0 ? void 0 : signUpMessage.includes('email')) {
                throw new Error(EXISTING_CREDENTIALS, { cause: error });
            }
            throw error;
        })
            .finally(() => {
            this.status.isAuthenticating = false;
        }));
    }
    /**
     * @return {Promise<SessionManager>}
     */
    async signOut() {
        if (!this.session) {
            return Promise.resolve(this);
        }
        return this.session.signOut()
            .then(() => {
            delete this.session;
            this.permissionManager = new PermissionManager();
            this.preferencesManager = new PreferencesManager();
            this.featureFlagManager = new FeatureFlagManager(this.preferencesManager, new LocalStorageCache());
            return this;
        });
    }
    /**
     * @param {string} token
     * @return {Promise<SignUpTokenExchange>}
     */
    async verifyEmail(token) {
        if (!this.status.isAuthenticated) {
            return Promise.reject(new Error('Not Authenticated'));
        }
        return SignUpTokenExchange.create({ token });
    }
    /**
     * @param {string} email
     * @return {Promise<PasswordResetToken>}
     */
    async requestPasswordReset(email) {
        return PasswordResetToken.create({ email })
            .catch((error) => {
            var _a, _b;
            const passwordReset = ((_b = (_a = error.body) === null || _a === void 0 ? void 0 : _a.display_errors) !== null && _b !== void 0 ? _b : [])[0];
            if (passwordReset === null || passwordReset === void 0 ? void 0 : passwordReset.includes('User with email does not exist')) {
                throw new Error(UNKNOWN_CREDENTIALS, { cause: error });
            }
            throw error;
        });
    }
    /**
     * @param {string} token
     * @param {string} password
     * @return {Promise<PasswordResetExchange>}
     */
    async resetPassword(token, password) {
        return PasswordResetExchange.create({ token, password });
    }
    /**
     * @param {ApplicationSession} session
     * @return {Promise<SessionManager>}
     * @private
     */
    async setSession(session) {
        this.session = session;
        const { user } = session;
        return Promise.all([
            this.preferencesManager.loadFor(user),
            this.permissionManager.loadFor(user),
        ])
            .then(() => {
            return this;
        });
    }
    /**
     * @param {object} environment
     * @param {string} environment.domain
     * @param {string} environment.protocol
     */
    static configure({ protocol, domain }) {
        SessionManager.ApplicationSession.$AR.url = `${protocol}://data.${domain}/api/user/v1/dashboard-logins`;
        SessionManager.ApplicationToken.$AR.url = `${protocol}://data.${domain}/api/common/v1/tokens`;
        SessionManager.ApplicationUser.$AR.url = `${protocol}://data.${domain}/api/common/v1/users`;
        SessionManager.ProviderSession.$AR.url = `${protocol}://data.${domain}/api/user/v1/authwalk-logins`;
        SessionManager.ProviderUser.$AR.url = `${protocol}://auth.${domain}/api/user/v1/users`;
        SessionManager.PasswordResetExchange.$AR.url = `${protocol}://auth.${domain}/api/user/v1/password-reset-exchanges`;
        SessionManager.PasswordResetToken.$AR.url = `${protocol}://auth.${domain}/api/user/v1/password-reset-tokens`;
        SessionManager.SignUpTokenExchange.$AR.url = `${protocol}://auth.${domain}/api/user/v1/signup-token-exchanges`;
    }
}
SessionManager.ApplicationSession = ApplicationSession;
SessionManager.ApplicationToken = ApplicationToken;
SessionManager.ApplicationUser = ApplicationUser;
SessionManager.ProviderSession = ProviderSession;
SessionManager.ProviderUser = ProviderUser;
SessionManager.PasswordResetExchange = PasswordResetExchange;
SessionManager.PasswordResetToken = PasswordResetToken;
SessionManager.SignUpTokenExchange = SignUpTokenExchange;
export default SessionManager;
